import React, { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { LogOut, User as UserIcon, Bell, Mail, Menu, X, LayoutDashboard, Settings, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [mobileRentOpen, setMobileRentOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const rentActive = pathname === "/browse" || pathname.startsWith("/browse/")
    || pathname === "/parking" || pathname.startsWith("/parking/");

  const linkCls = ({ isActive }) =>
    "text-sm font-medium transition " +
    (isActive ? "text-[var(--zc-text)]" : "text-[var(--zc-text-2)] hover:text-[var(--zc-text)]");

  const rentTriggerCls =
    "text-sm font-medium transition inline-flex items-center gap-1 " +
    (rentActive ? "text-[var(--zc-text)]" : "text-[var(--zc-text-2)] hover:text-[var(--zc-text)]");

  return (
    <header className="glass-nav fixed top-0 left-0 right-0 z-40" data-testid="navbar">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="brand-logo" aria-label="Z-Cars home">
          <img
            src="/brand/wordmark.png"
            alt="Z-Cars"
            className="h-8 md:h-9 w-auto select-none"
            draggable="false"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <NavLink to="/" className={linkCls} data-testid="nav-home" end>Home</NavLink>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={rentTriggerCls}
                data-testid="nav-rent"
                aria-current={rentActive ? "page" : undefined}
              >
                Rent
                <ChevronDown size={14} className="opacity-70" aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" data-testid="nav-rent-menu">
              <DropdownMenuItem
                onClick={() => navigate("/browse")}
                data-testid="nav-browse"
                className={pathname.startsWith("/browse") ? "text-[var(--zc-text)] font-semibold" : undefined}
              >
                Cars
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/parking")}
                data-testid="nav-parking"
                className={pathname.startsWith("/parking") ? "text-[var(--zc-text)] font-semibold" : undefined}
              >
                Parking
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <NavLink to="/host" className={linkCls} data-testid="nav-host">Host</NavLink>
          <NavLink to="/loyalty" className={linkCls} data-testid="nav-loyalty">Rewards</NavLink>
          {user?.is_admin && <NavLink to="/dashboard/admin" className={linkCls} data-testid="nav-admin">Admin</NavLink>}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <button
                onClick={() => navigate("/notifications")}
                className="w-9 h-9 grid place-items-center zc-btn-ghost"
                aria-label="Notifications"
                data-testid="btn-notifications"
              >
                <Bell size={16} />
              </button>
              <button
                onClick={() => navigate("/profile/messages")}
                className="w-9 h-9 grid place-items-center zc-btn-ghost"
                aria-label="Email & SMS"
                title="Email & SMS"
                data-testid="btn-messages"
              >
                <Mail size={16} />
              </button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 px-3 h-9 zc-btn-ghost"
                    title="Account"
                    data-testid="btn-profile-menu"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt={user.display_name || user.name} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <UserIcon size={16} />
                    )}
                    <span className="text-sm">{user.display_name?.split(" ")[0] || user.name?.split(" ")[0] || "Me"}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="profile-menu">
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard size={14} /> Account dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/profile/settings")} data-testid="menu-settings">
                    <Settings size={14} /> Profile settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut size={14} /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link to="/login" className="px-4 h-9 grid place-items-center zc-btn-primary text-sm" data-testid="nav-login">
              Sign in
            </Link>
          )}
        </div>

        <button className="md:hidden zc-btn-ghost w-9 h-9 grid place-items-center" onClick={() => setOpen(o => !o)} aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} data-testid="nav-mobile-toggle">
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[var(--zc-border)] bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            <NavLink to="/" className="text-sm" onClick={() => setOpen(false)} data-testid="m-nav-home" end>Home</NavLink>
            <div>
              <button
                type="button"
                className={
                  "text-sm flex items-center gap-1 " +
                  (rentActive ? "text-[var(--zc-text)] font-medium" : "text-[var(--zc-text)]")
                }
                onClick={() => setMobileRentOpen((o) => !o)}
                aria-expanded={mobileRentOpen}
                data-testid="m-nav-rent"
              >
                Rent
                <ChevronDown
                  size={14}
                  className={"opacity-70 transition " + (mobileRentOpen ? "rotate-180" : "")}
                  aria-hidden
                />
              </button>
              {(mobileRentOpen || rentActive) && (
                <div className="mt-2 ml-3 flex flex-col gap-2 border-l border-[var(--zc-border)] pl-3">
                  <NavLink to="/browse" className="text-sm" onClick={() => setOpen(false)} data-testid="m-nav-browse">Cars</NavLink>
                  <NavLink to="/parking" className="text-sm" onClick={() => setOpen(false)} data-testid="m-nav-parking">Parking</NavLink>
                </div>
              )}
            </div>
            <NavLink to="/host" className="text-sm" onClick={() => setOpen(false)} data-testid="m-nav-host">Host</NavLink>
            <NavLink to="/loyalty" className="text-sm" onClick={() => setOpen(false)} data-testid="m-nav-loyalty">Rewards</NavLink>
            {user?.is_admin && (
              <NavLink to="/dashboard/admin" className="text-sm" onClick={() => setOpen(false)} data-testid="m-nav-admin">Admin</NavLink>
            )}
            {user ? (
              <>
                <NavLink to="/profile/bookings" className="text-sm" onClick={() => setOpen(false)} data-testid="m-nav-trips">My bookings</NavLink>
                <NavLink to="/notifications" className="text-sm flex items-center gap-2" onClick={() => setOpen(false)} data-testid="m-nav-notifications">
                  <Bell size={14} /> Notifications
                </NavLink>
                <NavLink to="/profile/messages" className="text-sm flex items-center gap-2" onClick={() => setOpen(false)} data-testid="m-nav-messages">
                  <Mail size={14} /> Email & SMS
                </NavLink>
                <NavLink to="/profile/settings" className="text-sm" onClick={() => setOpen(false)} data-testid="m-nav-settings">Profile settings</NavLink>
                <NavLink to="/dashboard" className="text-sm" onClick={() => setOpen(false)} data-testid="m-nav-dash">Dashboard</NavLink>
                <button onClick={() => { setOpen(false); logout(); }} className="text-sm text-left" data-testid="m-nav-logout">Sign out</button>
              </>
            ) : (
              <NavLink to="/login" className="text-sm" onClick={() => setOpen(false)} data-testid="m-nav-login">Sign in</NavLink>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
